/**
 * Gatekeeper stage examinations — timed, tasked, scored like a real exam.
 * Blind piano: no green key guides. Staff + note names still shown so the student knows what to play.
 */

import type { MusicPiece } from "../music/types.js";
import {
  FollowAlongEngine,
  buildExpectedSteps,
  type FollowAlongStats,
} from "../practice/FollowAlongEngine.js";
import { getPieceById } from "../music/library.js";
import { getStage } from "../curriculum/curriculum.js";
import { midiToNoteName } from "../audio/midiMath.js";

/** Highest stage in the curriculum (exams unlock through Stage MAX). */
export const MAX_CURRICULUM_STAGE = 12;

/** Stages 1–9 count for Course Completion Certificate (server manifest stays 1–9). */
export const COURSE_CERTIFICATE_MAX_STAGE = 9;

/** Blueprint / UI: minimum accuracy to pass any Gatekeeper. */
export const EXAM_PASS_ACCURACY_PCT = 85;

export interface ExamDefinition {
  id: string;
  stage: number;
  title: string;
  /** Minimum accuracy % to pass (blueprint: ≥85%). */
  passAccuracyPct: number;
  /** Hard wall-clock limit (seconds). Expire → fail. */
  timeLimitSec: number;
  /** Piece ids the student must complete in sequence. */
  pieceIds: string[];
  /** Short overview for the briefing screen. */
  description: string;
  /** Bullet rules shown before Begin. */
  rules: string[];
  /** What to do on each piece (same length as pieceIds when set). */
  pieceTasks?: string[];
}

function midiLabel(midi: number): string {
  const { name, octave } = midiToNoteName(midi);
  return `${name}${octave}`;
}

/** Seed exams — denser piece lists aligned with expanded library. */
export const STAGE_EXAMS: ExamDefinition[] = [
  {
    id: "exam-stage-1",
    stage: 1,
    title: "Stage 1 Gatekeeper",
    passAccuracyPct: 85,
    timeLimitSec: 8 * 60,
    pieceIds: ["ex-five-finger-c", "scale-c-major-rh", "melody-ode-to-joy"],
    description: "Prove C-position fluency: drill, scale, and a short melody — no green key highlights.",
    rules: [
      "Play 3 tasks in order (shown on staff + note names).",
      "Piano keys stay blind — no green guides.",
      "Need ≥85% accuracy across all tasks.",
      "Finish before the timer ends or you fail.",
    ],
    pieceTasks: [
      "Task 1 — Five-finger walk in C: play each expected note/chord in order.",
      "Task 2 — C major scale (RH): ascending then descending.",
      "Task 3 — Ode to Joy melody: pitch-accurate follow-along.",
    ],
  },
  {
    id: "exam-stage-2",
    stage: 2,
    title: "Stage 2 Gatekeeper",
    passAccuracyPct: 85,
    timeLimitSec: 10 * 60,
    pieceIds: ["scale-g-major-rh", "scale-d-major-rh", "arp-c-major", "rhythm-syncop-offbeat"],
    description: "New keys + syncopation under exam pressure.",
    rules: [
      "4 tasks: G major, D major, C arpeggio, syncopation.",
      "No green piano guides — read staff / note names.",
      "≥85% aggregate accuracy · finish within the timer.",
    ],
    pieceTasks: [
      "Task 1 — G major scale RH.",
      "Task 2 — D major scale RH.",
      "Task 3 — C major arpeggio.",
      "Task 4 — Syncopated off-beat rhythm pattern.",
    ],
  },
  {
    id: "exam-stage-3",
    stage: 3,
    title: "Stage 3 Gatekeeper",
    passAccuracyPct: 85,
    timeLimitSec: 10 * 60,
    pieceIds: ["chord-c-major-triad", "chord-c-inv1", "chord-i-iv-v-c", "both-hand-alternation"],
    description: "Harmony & early independence checkpoint.",
    rules: [
      "Triads, inversion, I–IV–V cadence, both-hand alternation.",
      "Blind keys · staff + names on · ≥85% · timed.",
    ],
    pieceTasks: [
      "Task 1 — C major triad blocks.",
      "Task 2 — C triad 1st inversion.",
      "Task 3 — I–IV–V–I in C.",
      "Task 4 — Hands alternating notes.",
    ],
  },
  {
    id: "exam-stage-4",
    stage: 4,
    title: "Stage 4 Gatekeeper",
    passAccuracyPct: 85,
    timeLimitSec: 9 * 60,
    pieceIds: ["melody-london-bridge", "ex-interval-jumps", "ex-blind-scale-c"],
    description: "Sight / interval / memory gate.",
    rules: [
      "Melody, interval jumps, then a memory-leaning C scale.",
      "≥85% · timed · no green key helpers.",
    ],
    pieceTasks: [
      "Task 1 — London Bridge melody.",
      "Task 2 — Interval jump drill.",
      "Task 3 — C scale without relying on guides.",
    ],
  },
  {
    id: "exam-stage-5",
    stage: 5,
    title: "Stage 5 Gatekeeper",
    passAccuracyPct: 85,
    timeLimitSec: 12 * 60,
    pieceIds: [
      "scale-burst-c-sixteenths",
      "scale-burst-g-sixteenths",
      "arp-burst-c-2oct",
      "ex-scales-in-thirds-c",
    ],
    description: "Elite speed/accuracy gate.",
    rules: [
      "Bursts + thirds under time pressure.",
      "≥85% aggregate · finish before time expires.",
    ],
    pieceTasks: [
      "Task 1 — C scale burst (sixteenths feel).",
      "Task 2 — G scale burst.",
      "Task 3 — C arpeggio 2-octave burst.",
      "Task 4 — Scales in thirds (C).",
    ],
  },
  {
    id: "exam-stage-6",
    stage: 6,
    title: "Stage 6 Gatekeeper — Harmony Lab",
    passAccuracyPct: 85,
    timeLimitSec: 12 * 60,
    pieceIds: ["chord-c7-dom", "prog-ii-v-i-c", "circle-fifths-triads", "voicing-shell-c7"],
    description: "Extended harmony: sevenths, ii–V–I, circle, shells.",
    rules: [
      "4 harmony tasks — no green keys.",
      "Read staff / next-note names carefully (chords = all notes together).",
      "≥85% · timed exam.",
    ],
    pieceTasks: [
      "Task 1 — C7 dominant blocks (C–E–G–Bb together).",
      "Task 2 — ii–V–I in C (Dm → G → C).",
      "Task 3 — Circle-of-fifths triad march excerpt.",
      "Task 4 — C7 shell voicing (E + Bb).",
    ],
  },
  {
    id: "exam-stage-7",
    stage: 7,
    title: "Stage 7 Gatekeeper — Master Artistry",
    passAccuracyPct: 85,
    timeLimitSec: 14 * 60,
    pieceIds: [
      "scale-bb-major-rh",
      "etude-c-ascending-break",
      "both-held-bass-independence",
      "rhythm-perfect-quarters-locked",
    ],
    description: "Keys, etude, independence, timing — final master gate.",
    rules: [
      "Bb scale, mini-etude, held-bass independence, locked quarters.",
      "Blind piano · staff + names · ≥85% · finish before timer.",
    ],
    pieceTasks: [
      "Task 1 — Bb major scale RH.",
      "Task 2 — Mini etude ascending breaks.",
      "Task 3 — Held-bass independence (LH + RH together where marked).",
      "Task 4 — Timing drill: locked quarter notes (pitch still graded).",
    ],
  },
  {
    id: "exam-stage-8",
    stage: 8,
    title: "Stage 8 Gatekeeper — Complete Musicianship",
    passAccuracyPct: 85,
    timeLimitSec: 16 * 60,
    pieceIds: [
      "sight-unseen-b",
      "ear-intervals-complete",
      "pedal-change-foundations",
      "repertoire-classical-alberti",
    ],
    description: "Reading, aural memory, pedal awareness and a complete classical miniature.",
    rules: [
      "Four tasks: unseen reading, interval echo, pedal harmony and complete repertoire.",
      "No green key guides · ≥85% aggregate pitch accuracy · timed.",
      "MIDI sustain-pedal use is reported when available; pitch remains playable without hardware pedal.",
    ],
    pieceTasks: [
      "Task 1 — Unseen two-staff reading.",
      "Task 2 — Interval echo from memory.",
      "Task 3 — Clean pedal-change harmony.",
      "Task 4 — Complete Classical Alberti Miniature.",
    ],
  },
  {
    id: "exam-stage-9",
    stage: 9,
    title: "Final Gatekeeper — Recorded Recital",
    passAccuracyPct: 88,
    timeLimitSec: 20 * 60,
    pieceIds: [
      "tech-major-scales-sharp-keys",
      "tech-minor-scales-harmonic",
      "sight-transposition-challenge",
      "performance-final-capstone",
    ],
    description: "Final technique, advanced sight-reading and uninterrupted recital capstone.",
    rules: [
      "Four tasks: major keys, harmonic minors, unseen reading and final complete work.",
      "Blind piano · timing/dynamics reported · ≥88% pitch accuracy · timed.",
      "This is the final requirement for the Course Completion Certificate.",
    ],
    pieceTasks: [
      "Task 1 — Major-scale sharp-key circuit.",
      "Task 2 — Harmonic-minor circuit.",
      "Task 3 — Advanced unseen line in E-flat.",
      "Task 4 — Final Recital Capstone: complete uninterrupted take.",
    ],
  },
  {
    id: "exam-stage-10",
    stage: 10,
    title: "Stage 10 Gatekeeper — Repertoire",
    passAccuracyPct: 88,
    timeLimitSec: 18 * 60,
    pieceIds: [
      "pro-fugue-subject-drill",
      "pro-baroque-invention-study",
      "pro-classical-sonatina-a",
    ],
    description: "Professional repertoire track checkpoint.",
    rules: [
      "Three complete studies: subject, invention, sonatina exposition.",
      "No green key guides · ≥88% · timed.",
    ],
    pieceTasks: [
      "Task 1 — Fugue subject from memory.",
      "Task 2 — Two-voice invention study.",
      "Task 3 — Classical sonatina exposition.",
    ],
  },
  {
    id: "exam-stage-11",
    stage: 11,
    title: "Stage 11 Gatekeeper — Virtuoso Technique",
    passAccuracyPct: 88,
    timeLimitSec: 16 * 60,
    pieceIds: ["pro-double-thirds-c", "pro-octave-etude", "pro-arp-storm"],
    description: "Technique circuit under exam pressure.",
    rules: [
      "Thirds, octaves, arpeggio storm — blind keys · ≥88% · timed.",
    ],
    pieceTasks: [
      "Task 1 — Double thirds circuit.",
      "Task 2 — Octave etude.",
      "Task 3 — Arpeggio storm.",
    ],
  },
  {
    id: "exam-stage-12",
    stage: 12,
    title: "Professional Diploma Gatekeeper",
    passAccuracyPct: 90,
    timeLimitSec: 22 * 60,
    pieceIds: [
      "pro-sight-open-score",
      "pro-mock-jury-a",
      "pro-final-diploma-recital",
    ],
    description: "Final professional-track diploma exam.",
    rules: [
      "Sight line, mock jury work, diploma capstone.",
      "Blind piano · ≥90% · timed.",
    ],
    pieceTasks: [
      "Task 1 — Advanced sight line.",
      "Task 2 — Mock jury work A.",
      "Task 3 — Professional diploma recital capstone.",
    ],
  },
];

export function getExamById(id: string): ExamDefinition | undefined {
  return STAGE_EXAMS.find((e) => e.id === id);
}

export function getExamForStage(stage: number): ExamDefinition | undefined {
  return STAGE_EXAMS.find((e) => e.stage === stage);
}

/** Briefing payload for UI (before Start). */
export function getExamBriefing(stage: number): {
  exam: ExamDefinition;
  pieces: Array<{ id: string; title: string; task: string }>;
} | null {
  const exam = getExamForStage(stage);
  if (!exam) return null;
  const pieces = exam.pieceIds.map((id, i) => {
    const p = getPieceById(id);
    return {
      id,
      title: p?.title ?? id,
      task: exam.pieceTasks?.[i] ?? `Play: ${p?.title ?? id}`,
    };
  });
  return { exam, pieces };
}

export type ExamPhase = "idle" | "running" | "passed" | "failed";
export type ExamFailReason = "timeout" | "accuracy" | "missing_piece" | null;

export interface ExamStatus {
  phase: ExamPhase;
  examId: string | null;
  title: string | null;
  stage: number | null;
  /** Blind piano guides — UI must hide finger/letter key highlights. */
  blind: boolean;
  pieceIndex: number;
  pieceCount: number;
  currentPieceId: string | null;
  currentPieceTitle: string | null;
  /** Current task instruction for the HUD. */
  currentTask: string | null;
  expectedMidis: number[];
  /** Human-readable next notes e.g. ["C4","E4","G4"]. */
  nextNoteLabels: string[];
  remainingSteps: number;
  timeLimitSec: number;
  elapsedSec: number;
  remainingSec: number;
  stats: FollowAlongStats;
  passAccuracyPct: number;
  failReason: ExamFailReason;
  description: string | null;
}

export interface ProgressState {
  /** Highest stage the student may enter (1–MAX). */
  unlockedStage: number;
  completedClassIds: string[];
  passedExamIds: string[];
}

export const DEFAULT_PROGRESS: ProgressState = {
  unlockedStage: 1,
  completedClassIds: [],
  passedExamIds: [],
};

export class ExamSession {
  private exam: ExamDefinition | null = null;
  private phase: ExamPhase = "idle";
  private pieceIndex = 0;
  private follow = new FollowAlongEngine();
  private aggregateHits = 0;
  private aggregateWrongs = 0;
  private aggregatePerfect = 0;
  private aggregateEarly = 0;
  private aggregateLate = 0;
  private aggregateVelOk = 0;
  private aggregateVelMiss = 0;
  private aggregateErrors: Record<string, number> = {};
  private frozenMeanDelta = 0;
  private frozenJitter = 0;
  private startedAtMs = 0;
  private elapsedFrozenSec: number | null = null;
  private failReason: ExamFailReason = null;
  /** Audio clock origin for timing grades (set on first graded note). */
  private timelineOrigin: number | null = null;

  getFollowAlong(): FollowAlongEngine {
    return this.follow;
  }

  private wallElapsedSec(): number {
    if (this.elapsedFrozenSec != null) return this.elapsedFrozenSec;
    if (this.phase === "running" && this.startedAtMs > 0) {
      return (performance.now() - this.startedAtMs) / 1000;
    }
    return 0;
  }

  private freezeElapsed(): void {
    if (this.startedAtMs > 0 && this.elapsedFrozenSec == null) {
      this.elapsedFrozenSec = (performance.now() - this.startedAtMs) / 1000;
    }
  }

  getStatus(): ExamStatus {
    const stats = this.follow.getStats();
    const judged = this.aggregateHits + this.aggregateWrongs + stats.hits + stats.wrongs;
    const hits = this.aggregateHits + stats.hits;
    const wrongs = this.aggregateWrongs + stats.wrongs;
    const limit = this.exam?.timeLimitSec ?? 0;
    const elapsed = this.wallElapsedSec();
    const remaining = limit > 0 ? Math.max(0, limit - elapsed) : 0;
    const midis = this.follow.getExpectedMidiList();
    const pieceId = this.exam?.pieceIds[this.pieceIndex] ?? null;
    const piece = pieceId ? getPieceById(pieceId) : undefined;
    const priorRemaining = this.exam
      ? this.exam.pieceIds.slice(this.pieceIndex + 1).reduce((n, id) => {
          const p = getPieceById(id);
          return n + (p ? buildStepCount(p) : 0);
        }, 0)
      : 0;

    const aggStats = {
      ...stats,
      hits,
      wrongs,
      accuracyPct: judged ? (hits / judged) * 100 : 100,
      completed: this.phase === "passed" || this.phase === "failed",
      perfect: this.aggregatePerfect + stats.perfect,
      early: this.aggregateEarly + stats.early,
      late: this.aggregateLate + stats.late,
      velocityHits: this.aggregateVelOk + stats.velocityHits,
      velocityMisses: this.aggregateVelMiss + stats.velocityMisses,
      errorByMidi: mergeErrorMaps(this.aggregateErrors, stats.errorByMidi),
      elapsedSec: elapsed,
      notesPerMinute: elapsed > 0.5 ? (hits / elapsed) * 60 : hits * 60,
      // Prefer live follow jitter while running; frozen after
      meanDeltaMs: stats.meanDeltaMs || this.frozenMeanDelta,
      jitterMs: stats.jitterMs || this.frozenJitter,
    };

    return {
      phase: this.phase,
      examId: this.exam?.id ?? null,
      title: this.exam?.title ?? null,
      stage: this.exam?.stage ?? null,
      blind: this.phase === "running",
      pieceIndex: this.pieceIndex,
      pieceCount: this.exam?.pieceIds.length ?? 0,
      currentPieceId: pieceId,
      currentPieceTitle: piece?.title ?? null,
      currentTask:
        this.exam?.pieceTasks?.[this.pieceIndex] ??
        (piece ? `Play: ${piece.title}` : null),
      expectedMidis: midis,
      nextNoteLabels: midis.map(midiLabel),
      remainingSteps: (this.phase === "running" ? stats.remaining : 0) + priorRemaining,
      timeLimitSec: limit,
      elapsedSec: elapsed,
      remainingSec: remaining,
      stats: aggStats,
      passAccuracyPct: this.exam?.passAccuracyPct ?? EXAM_PASS_ACCURACY_PCT,
      failReason: this.failReason,
      description: this.exam?.description ?? null,
    };
  }

  start(examId: string): MusicPiece {
    const exam = getExamById(examId);
    if (!exam) throw new Error(`Unknown exam: ${examId}`);
    const piece = getPieceById(exam.pieceIds[0]);
    if (!piece) throw new Error(`Exam piece missing: ${exam.pieceIds[0]}`);
    this.exam = exam;
    this.phase = "running";
    this.pieceIndex = 0;
    this.aggregateHits = 0;
    this.aggregateWrongs = 0;
    this.aggregatePerfect = 0;
    this.aggregateEarly = 0;
    this.aggregateLate = 0;
    this.aggregateVelOk = 0;
    this.aggregateVelMiss = 0;
    this.aggregateErrors = {};
    this.frozenMeanDelta = 0;
    this.frozenJitter = 0;
    this.startedAtMs = performance.now();
    this.elapsedFrozenSec = null;
    this.failReason = null;
    this.timelineOrigin = null;
    this.follow.load(piece);
    return piece;
  }

  /** Call ~1Hz from UI. Times out → failed. Returns true if phase changed. */
  tick(): boolean {
    if (this.phase !== "running" || !this.exam) return false;
    if (this.wallElapsedSec() >= this.exam.timeLimitSec) {
      this.failReason = "timeout";
      this.phase = "failed";
      this.freezeElapsed();
      this.follow.clear();
      return true;
    }
    return false;
  }

  handleNoteOn(
    midi: number,
    opts: {
      velocity?: number;
      audioTime?: number;
      latencyCompSec?: number;
    } = {},
  ) {
    if (this.phase !== "running") return null;
    if (this.tick()) return null;
    const piece = getPieceById(this.exam!.pieceIds[this.pieceIndex]);
    if (opts.audioTime != null && this.timelineOrigin == null) {
      this.timelineOrigin = opts.audioTime;
    }
    const result = this.follow.handleNoteOn(midi, {
      velocity: opts.velocity,
      audioTime: opts.audioTime,
      bpm: piece?.bpm,
      timelineOrigin: this.timelineOrigin ?? undefined,
      gradeTiming: true,
      gradeVelocity: true,
      latencyCompSec: opts.latencyCompSec ?? 0,
      velocityWindow: 30,
    });
    if (this.follow.getStats().completed) {
      const s = this.follow.getStats();
      this.aggregateHits += s.hits;
      this.aggregateWrongs += s.wrongs;
      this.aggregatePerfect += s.perfect;
      this.aggregateEarly += s.early;
      this.aggregateLate += s.late;
      this.aggregateVelOk += s.velocityHits;
      this.aggregateVelMiss += s.velocityMisses;
      this.aggregateErrors = mergeErrorMaps(this.aggregateErrors, s.errorByMidi);
      this.frozenMeanDelta = s.meanDeltaMs;
      this.frozenJitter = s.jitterMs;
      this.advanceOrFinish();
    }
    return result;
  }

  private advanceOrFinish(): void {
    if (!this.exam) return;
    this.pieceIndex += 1;
    if (this.pieceIndex >= this.exam.pieceIds.length) {
      const judged = this.aggregateHits + this.aggregateWrongs;
      const acc = judged ? (this.aggregateHits / judged) * 100 : 0;
      if (acc >= this.exam.passAccuracyPct) {
        this.phase = "passed";
        this.failReason = null;
      } else {
        this.phase = "failed";
        this.failReason = "accuracy";
      }
      this.freezeElapsed();
      this.follow.clear();
      return;
    }
    const nextId = this.exam.pieceIds[this.pieceIndex];
    const piece = getPieceById(nextId);
    if (!piece) {
      this.phase = "failed";
      this.failReason = "missing_piece";
      this.freezeElapsed();
      return;
    }
    this.timelineOrigin = null;
    this.follow.load(piece);
  }

  stop(): void {
    this.phase = "idle";
    this.exam = null;
    this.follow.clear();
    this.pieceIndex = 0;
    this.startedAtMs = 0;
    this.elapsedFrozenSec = null;
    this.failReason = null;
    this.timelineOrigin = null;
  }
}

function mergeErrorMaps(
  a: Record<string, number>,
  b: Record<string, number>,
): Record<string, number> {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) out[k] = (out[k] ?? 0) + v;
  return out;
}

function buildStepCount(piece: MusicPiece): number {
  return buildExpectedSteps(piece).length;
}

/** Apply exam pass → unlock next stage (classes optional). */
export function applyExamPass(progress: ProgressState, examId: string): ProgressState {
  const exam = getExamById(examId);
  if (!exam) return progress;
  const passed = new Set(progress.passedExamIds);
  passed.add(examId);
  const nextStage = Math.min(MAX_CURRICULUM_STAGE, exam.stage + 1);
  return {
    ...progress,
    passedExamIds: [...passed],
    unlockedStage: Math.max(progress.unlockedStage, nextStage),
  };
}

export function applyClassComplete(progress: ProgressState, classId: string): ProgressState {
  const set = new Set(progress.completedClassIds);
  set.add(classId);
  return { ...progress, completedClassIds: [...set] };
}

/** True if every class in the stage is marked complete (XP / map UI; not required for exams). */
export function stageClassesCompleted(progress: ProgressState, stage: number): boolean {
  const st = getStage(stage);
  if (!st) return false;
  return st.classes.every((c) => progress.completedClassIds.includes(c.id));
}
